import React, { useEffect, useState } from 'react';
import './pwa.css';

const DISMISSED_KEY = 'mt-install-dismissed';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/** Läuft die Seite bereits als installierte App? */
export function isStandalone() {
  return (
    window.navigator.standalone === true ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
}

/** iPhone oder iPad - dort gibt es keinen Installations-Dialog. */
export function isIOS() {
  const ua = navigator.userAgent || '';
  // iPadOS meldet sich seit Version 13 als Macintosh; der Touchscreen ist das
  // einzige verlässliche Unterscheidungsmerkmal.
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || iPadOS;
}

function dismissedRecently() {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < COOLDOWN_MS;
  } catch {
    // Privater Modus o. ä. - dann eben ohne Gedächtnis.
    return false;
  }
}

function remember() {
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    /* ignorieren */
  }
}

/**
 * Hinweis, dass sich die Seite als App einrichten lässt.
 *
 * Zwei Wege, weil die Browser sich grundlegend unterscheiden:
 *
 *   Android/Chrome  löst `beforeinstallprompt` aus. Das Ereignis wird
 *                   aufgehoben und beim Klick auf „Installieren" ausgelöst -
 *                   ein Dialog des Browsers, kein eigener.
 *   iOS/Safari      kennt kein solches Ereignis. Dort bleibt nur, den Weg zu
 *                   erklären: Teilen-Symbol → „Zum Home-Bildschirm".
 *
 * Der Hinweis erscheint nur auf schmalen Bildschirmen (siehe pwa.css): Eine
 * App auf dem Desktop zu installieren ist möglich, aber nicht das, worum es
 * hier geht.
 */
export default function InstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [mode, setMode] = useState(null); // 'android' | 'ios' | null

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return undefined;

    function onBeforeInstallPrompt(event) {
      // Ohne preventDefault zeigt Chrome eine eigene Leiste am unteren Rand.
      event.preventDefault();
      setPrompt(event);
      setMode('android');
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    // iOS meldet sich nie von selbst - dort wird der Hinweis direkt gezeigt.
    if (isIOS()) setMode('ios');

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  async function install() {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    // Das Ereignis lässt sich nur einmal verwenden.
    setPrompt(null);
    setMode(null);
  }

  function dismiss() {
    remember();
    setMode(null);
  }

  if (!mode) return null;

  return (
    <div className="mt-pwa" role="region" aria-label="App installieren">
      <img className="mt-pwa__icon" src="/img/app-192.png" alt="" width="44" height="44" />

      <div className="mt-pwa__text">
        {mode === 'android' ? (
          <>
            <strong>Club Mini-Thrills als App</strong>
            <span>Direkt vom Startbildschirm öffnen — ohne Browserleiste.</span>
          </>
        ) : (
          <>
            <strong>Auf den Startbildschirm legen</strong>
            <span>
              Unten auf <span aria-hidden="true">􀈂</span> <em>Teilen</em> tippen, dann „Zum
              Home-Bildschirm“.
            </span>
          </>
        )}
      </div>

      <div className="mt-pwa__actions">
        {mode === 'android' && (
          <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={install}>
            Installieren
          </button>
        )}
        <button
          type="button"
          className="mt-btn mt-btn--ghost mt-btn--sm"
          onClick={dismiss}
          aria-label="Hinweis ausblenden"
        >
          Später
        </button>
      </div>
    </div>
  );
}
